/**
 * useYjsCollaboration React Hook
 *
 * Unified hook for Yjs collaborative editing.
 * Combines useYjsSync and useYjsExtension into a single convenient API.
 *
 * @module features/collab/yjs/hooks/useYjsCollaboration
 * @see SPEC-008 Requirements 2.1, 2.3, 6.1-6.9
 */

import * as Y from 'yjs';
import { useMemo } from 'react';
import type { Extension } from '@codemirror/state';
import type { SyncState } from '@linkode/shared';
import { useYjsSync, type UseYjsSyncOptions } from './useYjsSync';
import { useYjsExtension, type UseYjsExtensionOptions } from './useYjsExtension';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useYjsCollaboration hook
 */
export interface UseYjsCollaborationOptions extends UseYjsSyncOptions, UseYjsExtensionOptions {
  /**
   * Whether Yjs collaboration is enabled.
   * When false, the hook returns null values and doesn't connect.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Return type for useYjsCollaboration hook
 */
export interface UseYjsCollaborationResult {
  /** Y.Doc instance for editor binding */
  yDoc: Y.Doc | null;
  /** Y.Text instance for direct text access (key: 'content') */
  yText: Y.Text | null;
  /** CodeMirror extension for Yjs binding */
  extension: Extension | null;
  /** Current sync state */
  state: SyncState;
  /** Whether the sync is ready (synced state) */
  isReady: boolean;
  /** Whether there's an error */
  hasError: boolean;
  /** Error if sync failed */
  error: Error | null;
  /** Whether currently syncing (has pending updates) */
  isSyncing: boolean;
  /** Whether disconnected (offline) */
  isOffline: boolean;
  /** Manually trigger reconnect */
  reconnect: () => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Unified React hook for Yjs collaborative editing.
 *
 * Combines sync management and CodeMirror extension creation into a single hook.
 * Handles the full lifecycle of Yjs collaboration:
 * 1. Creates YjsSyncManager for server synchronization
 * 2. Bootstraps document state from server
 * 3. Creates CodeMirror extension with yCollab binding
 * 4. Provides Y.Text for Milkdown/WYSIWYG integration
 *
 * @param docId - Document ID to sync
 * @param userId - Current user's ID
 * @param options - Configuration options
 * @returns Collaboration state and editor integration objects
 *
 * @example
 * ```typescript
 * function Editor({ docId }: { docId: string }) {
 *   const {
 *     yDoc,
 *     yText,
 *     extension,
 *     state,
 *     isReady,
 *     isOffline,
 *     error,
 *   } = useYjsCollaboration(docId, userId, {
 *     enabled: isCloudMode && isAuthenticated,
 *     initialContent: loadedMarkdown,
 *   });
 *
 *   if (!isReady && !isOffline) return <div>Connecting...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <>
 *       <EditorShell additionalExtensions={extension ? [extension] : undefined} />
 *       <WysiwygPreview yText={yText} />
 *     </>
 *   );
 * }
 * ```
 */
export function useYjsCollaboration(
  docId: string,
  userId: string,
  options: UseYjsCollaborationOptions = {}
): UseYjsCollaborationResult {
  const {
    enabled = true,
    captureTimeout,
    autoConnect = true,
    initialContent,
    ...syncOptions
  } = options;

  // Use Yjs sync hook (disabled if not enabled)
  const { yDoc, yText, state, error, isReady, reconnect } = useYjsSync(docId, userId, {
    ...syncOptions,
    autoConnect: enabled && autoConnect,
    initialContent,
  });

  // Use Yjs extension hook (creates CodeMirror binding)
  // Pass null awareness since Presence is handled by SPEC-007
  const extensionResult = useYjsExtension(
    enabled ? yDoc : null,
    null, // awareness handled by SPEC-007 usePresence
    { captureTimeout }
  );

  // Extract extension from result
  const extension = useMemo(() => {
    return extensionResult?.extension ?? null;
  }, [extensionResult]);

  // Computed states
  const isSyncing = state === 'syncing';
  const isOffline = state === 'disconnected' || state === 'error';
  const hasError = error !== null;

  return {
    yDoc: enabled ? yDoc : null,
    yText: enabled ? yText : null,
    extension,
    state,
    isReady,
    hasError,
    error,
    isSyncing,
    isOffline,
    reconnect,
  };
}
